import {
  DEFAULT_JACKPOT_FORM,
  DEFAULT_METADATA_FORM,
  DEFAULT_PROMO_FORM,
  DEFAULT_SOURCE_FORM,
} from './ruleConstants'
import {
  formatDateTimeLocal,
  itemIsActive,
  listToText,
  numberOrEmpty,
  numericOrNull,
  parseTags,
  toIsoDateTime,
} from './formatters'

export function promoToForm(item) {
  return {
    ...DEFAULT_PROMO_FORM,
    id: item.id,
    promo_id: item.promo_id || '',
    name: item.name || '',
    promo_type: item.promo_type || DEFAULT_PROMO_FORM.promo_type,
    status: item.status || DEFAULT_PROMO_FORM.status,
    eligible_game_ids: listToText(item.eligible_game_ids),
    priority: item.priority ?? DEFAULT_PROMO_FORM.priority,
    market: item.market || '',
    segment: item.segment || '',
    starts_at: formatDateTimeLocal(item.starts_at),
    ends_at: formatDateTimeLocal(item.ends_at),
    is_active: itemIsActive(item),
  }
}

export function jackpotToForm(item) {
  return {
    ...DEFAULT_JACKPOT_FORM,
    id: item.id,
    pool_id: item.pool_id || '',
    jackpot_type: item.jackpot_type || DEFAULT_JACKPOT_FORM.jackpot_type,
    label: item.label || '',
    current_amount: numberOrEmpty(item.current_amount),
    currency: item.currency || DEFAULT_JACKPOT_FORM.currency,
    participating_game_ids: listToText(item.participating_game_ids),
    must_drop_at: formatDateTimeLocal(item.must_drop_at),
    last_payout_at: formatDateTimeLocal(item.last_payout_at),
    market: item.market || '',
    segment: item.segment || '',
    is_active: itemIsActive(item),
  }
}

export function metadataToForm(item) {
  const metadata = item.metadata || {}
  return {
    ...DEFAULT_METADATA_FORM,
    id: item.id,
    game_code: item.game_code || '',
    game_name: item.game_name || '',
    status: item.status || DEFAULT_METADATA_FORM.status,
    themes: listToText(metadata.theme_tags),
    mechanics: listToText(metadata.mechanic_tags),
    volatility: metadata.volatility || '',
    payout_level: numberOrEmpty(metadata.payout_level),
    min_bet: numberOrEmpty(metadata.min_bet),
    max_bet: numberOrEmpty(metadata.max_bet),
    currency: metadata.currency || DEFAULT_METADATA_FORM.currency,
    max_win_multiplier: numberOrEmpty(metadata.max_win_multiplier),
    source_notes: item.source_notes || '',
    is_active: itemIsActive(item),
  }
}

export function sourceToForm(item) {
  return {
    ...DEFAULT_SOURCE_FORM,
    id: item.id,
    name: item.name || '',
    source_type: item.source_type || DEFAULT_SOURCE_FORM.source_type,
    status: item.status || DEFAULT_SOURCE_FORM.status,
    owner: item.owner || item.config?.owner || '',
    mapping_notes: item.mapping_notes || item.config?.mapping_notes || '',
    last_ingested_at: formatDateTimeLocal(item.last_ingested_at),
    market: item.market || '',
    segment: item.segment || '',
    is_active: itemIsActive(item),
  }
}

export function buildPromoPayload(form) {
  return {
    promo_id: form.promo_id.trim(),
    name: form.name.trim(),
    promo_type: form.promo_type.trim() || 'generic',
    status: form.status.trim() || 'active',
    eligible_game_ids: parseTags(form.eligible_game_ids),
    priority: Number(form.priority) || 0,
    market: form.market.trim() || null,
    segment: form.segment.trim() || null,
    starts_at: toIsoDateTime(form.starts_at),
    ends_at: toIsoDateTime(form.ends_at),
    is_active: Boolean(form.is_active),
    impact_config: {},
  }
}

export function buildJackpotPayload(form) {
  return {
    pool_id: form.pool_id.trim(),
    jackpot_type: form.jackpot_type || 'progressive',
    label: form.label.trim() || null,
    current_amount: numericOrNull(form.current_amount),
    currency: form.currency.trim() || 'USD',
    participating_game_ids: parseTags(form.participating_game_ids),
    must_drop_at: toIsoDateTime(form.must_drop_at),
    last_payout_at: toIsoDateTime(form.last_payout_at),
    market: form.market.trim() || null,
    segment: form.segment.trim() || null,
    is_active: Boolean(form.is_active),
    label_config: {},
  }
}

export function buildMetadataPayload(form) {
  const metadata = {
    theme_tags: parseTags(form.themes),
    mechanic_tags: parseTags(form.mechanics),
  }
  if (form.volatility) metadata.volatility = form.volatility
  if (numericOrNull(form.payout_level) != null) metadata.payout_level = numericOrNull(form.payout_level)
  if (numericOrNull(form.min_bet) != null) metadata.min_bet = numericOrNull(form.min_bet)
  if (numericOrNull(form.max_bet) != null) metadata.max_bet = numericOrNull(form.max_bet)
  if (form.currency.trim()) metadata.currency = form.currency.trim()
  if (numericOrNull(form.max_win_multiplier) != null) metadata.max_win_multiplier = numericOrNull(form.max_win_multiplier)
  return {
    game_code: form.game_code.trim(),
    game_name: form.game_name.trim() || null,
    status: form.status,
    metadata,
    confidence: {},
    source_notes: form.source_notes.trim() || null,
    is_active: Boolean(form.is_active),
  }
}

export function buildSourcePayload(form) {
  return {
    name: form.name.trim(),
    source_type: form.source_type.trim() || 'manual',
    status: form.status.trim() || 'active',
    owner: form.owner.trim() || null,
    mapping_notes: form.mapping_notes.trim() || null,
    config: {},
    schedule_config: {},
    last_ingested_at: toIsoDateTime(form.last_ingested_at),
    market: form.market.trim() || null,
    segment: form.segment.trim() || null,
    is_active: Boolean(form.is_active),
  }
}
